/* eslint-disable import/no-deprecated */
import { classNames } from '@fullstory/util/fmt';
import { Moment } from 'moment';
import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Popover } from '../../../components/Popover';
import { Typography } from '../../../deprecated/Typography';
import { Box } from '../../Box';
import { monthsInRange } from '../Calendar/util';
import { MomentRanges, RangeIndex } from '../types';
import { useScrollPercentage } from './hooks';
import css from './index.module.scss';
import { MinimapMonth } from './MinimapMonth';
import { MonthScroller } from './MonthScroller';
import { daysInMonths, getElementDimensions, getViewportScrollOffset } from './util';

export interface MinimapProps {
  /**
   * The selected/comparison date ranges. The `MinimapDay` will be rendered differently
   * based on if/where it falls within the provided ranges.
   */
  ranges: MomentRanges;
  /**
   * Controls the which range takes priority when rendering overlapping ranges.
   */
  focusedRange: RangeIndex;
  /**
   * The minimum selectable date. If provided, days before this date will be disabled.
   */
  minDate: Moment;
  /**
   * The maximum selectable date. If provided, days after this date will be disabled.
   */
  maxDate: Moment;
  /**
   * The viewport of the `Calendar` that this `Minimap` will be referencing.
   */
  viewport: HTMLDivElement | null;
  /**
   * Callback that fires when the `Minimap` highlight window is dragged.
   */
  onScrollChange: (scrollY: number) => void;
  /**
   * Optional function that controls whether a given day is disabled. Useful for
   * limiting selection to a minimum of 3 days, for example.
   */
  isDayDisabled?: (day: Moment) => boolean;
  /**
   * Optional function to render an info tooltip for a given day. Useful for visualizing
   * an orgs retention period, for example.
   */
  renderInfoTooltip?: (day: Moment) => ReactNode;
}

const Minimap: React.FC<MinimapProps> = ({
  ranges,
  focusedRange,
  minDate,
  maxDate,
  viewport,
  onScrollChange,
  isDayDisabled,
  renderInfoTooltip,
}) => {
  const months = useMemo(() => monthsInRange(minDate, maxDate), [minDate, maxDate]);
  const scrollPercentage = useScrollPercentage(viewport);
  const [dragging, setDragging] = useState(false);
  const [highlightEl, setHighlightEl] = useState<HTMLDivElement | null>(null);
  const minimapRef = useRef<HTMLDivElement>(null);
  const minimapContentRef = useRef<HTMLDivElement>(null);

  const { height: minimapContentHeight } = getElementDimensions(minimapContentRef.current);
  const { width: minimapWidth, height: minimapHeight } = getElementDimensions(minimapRef.current);
  const { width: viewportWidth, height: viewportHeight } = getElementDimensions(viewport);
  const viewportRatio = viewportWidth / viewportHeight;
  // highlight should match the viewport ratio
  const highlightHeight = minimapWidth / viewportRatio;

  // values derived from scroll percentage
  const highlightOffset = getViewportScrollOffset(scrollPercentage, minimapHeight, highlightHeight);
  const scrollOffset = getViewportScrollOffset(
    scrollPercentage,
    minimapContentHeight,
    minimapHeight,
  );
  // find the month to display by the handle based on the scroll percentage
  const allDays = useMemo(() => daysInMonths(months), [months]);
  const displayMonth = allDays[Math.floor((allDays.length - 1) * scrollPercentage)];

  const onDrag: (clientY: number) => void = useCallback(
    clientY => {
      if (!viewport || !minimapRef.current) return;
      const { top: minimapTop, height: minimapHeight } = minimapRef.current.getBoundingClientRect();
      const scrollTop = clientY - minimapTop - highlightHeight / 2;
      const scrollPercentage = scrollTop / (minimapHeight - highlightHeight);
      const scrollY = getViewportScrollOffset(
        scrollPercentage,
        viewport.scrollHeight,
        viewportHeight,
      );
      onScrollChange(scrollY);
    },
    [highlightHeight, onScrollChange, viewport, viewportHeight],
  );

  return (
    <Box
      data-component="Minimap"
      className={classNames(css.minimap, {
        [css.dragging]: dragging,
      })}
      ref={minimapRef}
    >
      <MonthScroller
        ref={minimapContentRef}
        scrollOffset={scrollOffset}
        dragging={dragging}
        setDragging={setDragging}
        onDrag={onDrag}
      >
        {months.map(month => (
          <MinimapMonth
            key={month.toISOString()}
            month={month}
            ranges={ranges}
            focusedRange={focusedRange}
            minDate={minDate}
            maxDate={maxDate}
            isDayDisabled={isDayDisabled}
            renderInfoTooltip={renderInfoTooltip}
          />
        ))}
      </MonthScroller>
      {highlightHeight && (
        <div
          ref={setHighlightEl}
          className={css.highlight}
          style={{
            height: highlightHeight,
            transform: `translateY(${highlightOffset}px)`,
          }}
        >
          <Popover
            variant="dark"
            placement="left"
            anchor={highlightEl}
            open={Boolean(dragging && displayMonth)}
            offset={8}
            arrow={false}
            portal={false}
          >
            <Box padding={[4, 8]}>
              <Typography
                variant="body3"
                color="neutrals-n0"
                textTransform="uppercase"
                whiteSpace="nowrap"
              >
                {displayMonth?.format('MMM YYYY')}
              </Typography>
            </Box>
          </Popover>
        </div>
      )}
    </Box>
  );
};

const MemoizedMinimap = React.memo(Minimap);

export { MemoizedMinimap as Minimap };
